<script setup lang="ts">
import { computed, inject, type PropType, watch } from "vue";
import type { UIColor } from "../../..";
import { SimpleTableContextInjectionKey } from "../../helpers.js";
import PushButton from "../../atoms/PushButton/PushButton.vue";
import Icon from "../../atoms/Icon/Icon.vue";

const props = defineProps({
  id: {
    type: String,
    default: "",
  },

  colSpan: {
    type: String,
    default: "1",
  },

  /*
      Should the row be fixed to the top
      values: true, false (default)
    */
  fixed: {
    type: Boolean,
    default: false,
  },

  /*
      Render row as a card
    */
  renderAsCard: {
    type: Boolean,
    default: false,
  },

  /*
      Background color
    */
  renderAsCardColorBackground: {
    type: String as PropType<UIColor>,
    default: "",
  },

  /*
      Collapses space on top of row, to visually extend the row above when used together with renderAsCard
    */
  renderAsCardExtension: {
    type: Boolean,
    default: false,
  },

  /*
      Set the background color on :hover
    */
  hoverEffectDisable: {
    type: Boolean,
    default: false,
  },

  /*
      Manually toggle hover state effect
    */
  hoverEffectStateActive: {
    type: Boolean,
    default: false,
  },

  /*
      Set the background color on :hover
    */
  hoverEffectColorBackground: {
    type: String as PropType<UIColor>,
    default: "--color-accent-200",
  },

  /*
      Set the border color on :hover
    */
  hoverEffectColorBorder: {
    type: String as PropType<UIColor>,
    default: "--color-accent-200",
  },

  /*
      Render as a totals row
    */
  totals: {
    type: Boolean,
    default: false,
  },

  onClickEdit: {
    type: Function as PropType<() => void>,
    required: false,
  },

  onClickDuplicate: {
    type: Function as PropType<() => void>,
    required: false,
  },

  onClickDelete: {
    type: Function as PropType<() => void>,
    required: false,
  },
});

const rowClicksBound = computed(
  () =>
    Number(!!props.onClickEdit) +
    Number(!!props.onClickDuplicate) +
    Number(!!props.onClickDelete)
);

const tableContext = inject(SimpleTableContextInjectionKey)!;

watch(
  () => props.renderAsCard,
  (v) => v && (tableContext.containsRenderAsCard.value = v),
  { immediate: true }
);
watch(
  () => props.renderAsCardExtension,
  (v) => v && (tableContext.containsRenderAsCardExtension.value = v),
  { immediate: true }
);
</script>

<template>
  <tr
    :id="id"
    :class="[
      $style.tr,
      'simple-table-row',
      fixed ? 'simple-table-row-fixed' : '',
      hoverEffectDisable ? 'hoverEffectDisable' : '',
      hoverEffectStateActive ? 'hoverEffectStateActive' : '',
      renderAsCard ? 'renderAsCard' : '',
      renderAsCardExtension ? 'renderAsCardExtension' : '',
      totals ? 'totals' : '',
    ]"
    :colspan="colSpan"
    :style="{
      '--simple-table-row-card-color-background': `var(${renderAsCardColorBackground})`,
      '--simple-table-row-hover-effect-color-background': `var(${hoverEffectColorBackground})`,
      '--simple-table-row-hover-effect-color-border': `var(${hoverEffectColorBorder})`,
      '--simple-table-row-controls-offset': `${rowClicksBound}rem`,
    }"
    v-bind="$attrs"
  >
    <td v-if="rowClicksBound > 0">
      <div class="simple-table-row-controls-outer">
        <div class="simple-table-row-controls-inner">
          <div v-if="onClickEdit" class="simple-table-row-control">
            <PushButton
              size="collapsed"
              color="--color-transparent"
              @click="onClickEdit"
            >
              <template #icon>
                <Icon id="edit" size="medium" color="--color-primary" />
              </template>
            </PushButton>
          </div>

          <div v-if="onClickDuplicate" class="simple-table-row-control">
            <PushButton
              size="collapsed"
              color="--color-transparent"
              @click="onClickDuplicate"
            >
              <template #icon>
                <Icon id="content_copy" size="medium" color="--color-primary" />
              </template>
            </PushButton>
          </div>

          <div v-if="onClickDelete" class="simple-table-row-control">
            <PushButton
              size="collapsed"
              color="--color-transparent"
              @click="onClickDelete"
            >
              <template #icon>
                <Icon id="close" size="medium" color="--color-primary" />
              </template>
            </PushButton>
          </div>
        </div>
      </div>
    </td>

    <td v-else />

    <slot />
  </tr>
</template>

<style module>
.tr {
  scroll-snap-align: start;
}

/* ---------- */

:global(.simple-table-row .simple-table-row-controls-outer) {
  display: flex;
  position: relative;
  height: calc(3rem - 1px);
  margin-top: 1px;
}

:global(.simple-table-row .simple-table-row-controls-inner) {
  display: flex;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: fit-content;
  padding-left: 1rem;
  z-index: 999999999;
  background-color: var(--simple-table-row-hover-effect-color-background);
  transition-property: transform, opacity;
  transition-duration: var(--transition-duration-shortest);
  transition-timing-function: ease-in-out;
  transform: translateX(
    calc(0px - var(--simple-table-row-controls-offset) * 1.75)
  );
  opacity: 0;
  overflow: hidden;
}

:global(.simple-table-row:hover .simple-table-row-controls-inner) {
  transform: translateX(0);
  opacity: 1;
}

:global(
    .simple-table-row
      .simple-table-cell:nth-child(2)
      .simple-table-cell-controls-container
  ) {
  transition-property: transform;
  transition-duration: var(--transition-duration-shortest);
  transition-timing-function: ease-in-out;
  padding-right: calc(var(--simple-table-row-controls-offset) * 1.75);
}

:global(
    .simple-table-row:hover
      .simple-table-cell:nth-child(2)
      .simple-table-cell-controls-container
  ) {
  transform: translateX(calc(var(--simple-table-row-controls-offset) * 1.75));
}

:global(.simple-table-row .simple-table-row-control) {
  display: flex;
  transition-property: transform;
  transition-duration: var(--transition-duration-shortest);
  transition-timing-function: ease-in-out;
}

:global(.simple-table-row .simple-table-row-control:hover) {
  transform: scale(1.25);
}

:global(.simple-table-row .simple-table-row-control:not(:last-child)) {
  margin-right: 0.5rem;
}

/* ---------- */

:global(.simple-table-row.renderAsCard) {
  background-color: var(--simple-table-row-card-color-background);
  clip-path: inset(0 0 0 0 round var(--border-radius-medium));
}

:global(.simple-table-row.renderAsCardExtension) {
  transform: translateY(-0.5rem);
}

:global(.simple-table-row.renderAsCardExtension td) {
  border-top: solid 1px var(--color-accent-200);
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

:global(.simple-table-row td) {
  z-index: 1;
}

:global(.simple-table-row.simple-table-row-fixed td) {
  position: sticky;
  top: 0;
  z-index: 2;
}

:global(.simple-table-row.totals) {
  border-top: solid 1px var(--color-accent-200);
  border-bottom: solid 1px var(--color-accent-200);
}

:global(.simple-table-row.totals td) {
  position: relative;
  box-shadow: none !important;
}
</style>
